import { Box, useMediaQuery, useTheme } from '@mui/material';
import { AppGlobalAlert } from 'app/components/AppGlobalAlert';
import { AppHeader } from 'app/components/AppHeader';
import { LeftSidebar } from 'app/components/Sidebar/LeftSidebar';
import { SidebarPanelRight } from 'app/components/Sidebar/SidebarPanel';
import { SidebarPanelMobile } from 'app/components/Sidebar/SidebarPanel/SidebarPanelMobile';
import { useAppCounters } from 'app/hooks';
import { AppGlobalAlertProvider, RightSidebarProvider } from 'app/providers';
import { lazyRetry } from 'app/utils';
import classNames from 'classnames';
import { BracketTournamentCodeModal } from 'modules/bracket/components/BracketTournamentCodeModal';
import { TournamentMatchDetailsModal } from 'modules/bracket/components/TournamentMatchDetailsModal';
import { ChatModal } from 'modules/chat/components/ChatModal';
import { ChatPopup } from 'modules/chat/components/ChatPopup';
import { ChatBoxProvider } from 'modules/chat/providers/ChatBoxProvider';
import { FeedbackModal } from 'modules/feedback/components/FeedbackModal';
import { MatchUrlQueryDetector } from 'modules/matches/components/MatchUrlQueryDetector';
import { TournamentUrlQueryDetector } from 'modules/tournaments/components/TournamentUrlQueryDetector';
import React, { FC, Suspense, useMemo } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { ActionGuardDialog } from 'shared/components/ActionGuardDialog';
import { PlatformLinkingDialog } from 'shared/components/PlatformLinkingDialog';
import { useStyles as scrollStyles } from 'shared/styles/ScrollBar.styles';
import { CreateAnnouncementModal } from 'modules/notifications/components/CreateAnnouncementModal';
import { Route } from 'app/enums';
import { TournamentGameActivity } from 'modules/tournament-activity/components/TournamentGameActivity';
import { AppMeta } from '../../AppMeta';
import { useStyles } from './AppLayout.styles';
import { OnlineDetectionNotifier } from 'shared/components/OnlineDetectionNotifier';

const AppAssets = lazyRetry(() => import('app/components/AppAssets/AppAssets'));

export const AppLayout: FC = () => {
  const theme = useTheme();
  const { classes } = useStyles();
  const { pathname } = useLocation();
  const { classes: scrollBarClasses } = scrollStyles();
  const isFullScreen = useMediaQuery(theme.breakpoints.up('lg'));

  const isBracketPage = useMemo(
    () => Boolean(matchPath(Route.TournamentBracket, pathname)),
    [pathname]
  );

  const isHomePage = useMemo(() => Boolean(matchPath(Route.Home, pathname)), [pathname]);

  const layoutWrapper = useMemo(() => {
    if (isBracketPage) {
      return (
        // Used to sync the scroll with the drag
        <ScrollContainer
          vertical={true}
          horizontal={false}
          hideScrollbars={false}
          ignoreElements="input"
          className={classNames(classes.scroll, scrollBarClasses.vertical)}
        >
          <Box className={classNames(classes.contentContainer)}>
            <Box className={classes.outlet}>
              <Outlet />
            </Box>
          </Box>
        </ScrollContainer>
      );
    }

    const outletClasses = isHomePage && !isFullScreen ? classes.mobileOutlet : classes.outlet;
    return (
      <Box className={classNames(classes.contentContainer, scrollBarClasses.vertical)}>
        <Box className={outletClasses}>
          <Outlet />
        </Box>
      </Box>
    );
  }, [classes, isBracketPage, isFullScreen, isHomePage, scrollBarClasses.vertical]);

  useAppCounters();

  return (
    <Box className={classes.root}>
      <AppMeta />
      <AppGlobalAlertProvider>
        {isFullScreen && <AppGlobalAlert />}
        <ChatBoxProvider>
          <RightSidebarProvider>
            <Box className={classes.mainContent}>
              <Box className={classes.wrapper}>
                <AppHeader />
                {/* TODO: add the tss class instead of main, when the right side bar menu will be updated */}
                <Box className={classNames(classes.mainContent, 'main')}>
                  <LeftSidebar />
                  <Box className={classes.content}>
                    <TournamentGameActivity />
                    {layoutWrapper}
                  </Box>
                  {isFullScreen ? <SidebarPanelRight /> : <SidebarPanelMobile />}
                </Box>
              </Box>
            </Box>
            <ChatPopup />
            <ChatModal />
            <FeedbackModal />
            <MatchUrlQueryDetector />
            <TournamentUrlQueryDetector />
            <OnlineDetectionNotifier />
            <TournamentMatchDetailsModal />
            <BracketTournamentCodeModal />
            <PlatformLinkingDialog />
            <ActionGuardDialog />
            <CreateAnnouncementModal />
          </RightSidebarProvider>
        </ChatBoxProvider>
        <Suspense fallback={null}>
          <AppAssets />
        </Suspense>
      </AppGlobalAlertProvider>
    </Box>
  );
};
